import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, Calendar, Gauge, Fuel, Settings2, Palette, 
  DoorOpen, Users, Zap, Cog, Shield, ChevronLeft, ChevronRight,
  Check, Car, MessageCircle, Phone, Mail
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "+390571902390";

export default function VehicleDetailPage() {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    loadVehicle();
  }, [id]);

  const loadVehicle = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/vehicles/${id}`);
      setVehicle(response.data);
    } catch (error) {
      console.error("Errore caricamento veicolo:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatMileage = (km) => {
    return new Intl.NumberFormat('it-IT').format(km) + ' km';
  };

  const getYear = (registration) => {
    if (!registration) return 'N/A';
    return registration.split('-')[0];
  };

  const getMonth = (registration) => {
    if (!registration) return '';
    const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
    const month = parseInt(registration.split('-')[1]) - 1;
    return months[month] || '';
  };

  const getFuelLabel = (fuel) => {
    const fuels = {
      'gasolio': 'Diesel',
      'benzina': 'Benzina',
      'metano': 'Metano',
      'gpl': 'GPL',
      'elettrico': 'Elettrico',
      'ibrido': 'Ibrido'
    };
    return fuels[fuel?.toLowerCase()] || fuel || 'N/A';
  };

  const nextImage = () => {
    if (vehicle?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % vehicle.images.length);
    }
  };

  const prevImage = () => {
    if (vehicle?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + vehicle.images.length) % vehicle.images.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-zinc-100 flex flex-col items-center justify-center p-6">
        <Car className="w-16 h-16 text-zinc-300 mb-4" />
        <h2 
          className="text-2xl font-bold text-zinc-700 mb-4"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          Veicolo non trovato
        </h2>
        <Link to="/catalogo">
          <Button className="bg-blue-600 text-white hover:bg-blue-700">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna al Catalogo
          </Button>
        </Link>
      </div>
    );
  }

  const images = vehicle.images?.length > 0 
    ? vehicle.images 
    : ['https://images.unsplash.com/photo-1655285162488-f495df383244?w=1200'];

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="vehicle-detail-page">
      {/* Breadcrumb */}
      <div className="bg-neutral-950 py-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link 
            to="/catalogo"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna al Catalogo
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-12">
          {/* Gallery - Left Side */}
          <div className="lg:sticky lg:top-24 lg:self-start mb-8 lg:mb-0">
            {/* Main Image */}
            <div className="relative aspect-[4/3] bg-zinc-200 overflow-hidden mb-4">
              <img
                src={images[currentImageIndex]}
                alt={`${vehicle.brand} ${vehicle.model}`}
                className="w-full h-full object-cover"
                data-testid="main-image"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1655285162488-f495df383244?w=1200';
                }}
              />
              
              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors"
                    data-testid="prev-image-btn"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors"
                    data-testid="next-image-btn"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white text-sm px-3 py-1">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                </>
              )}
            </div>

            {/* Thumbnail Grid */}
            {images.length > 1 && (
              <div className="grid grid-cols-6 gap-2" data-testid="thumbnail-grid">
                {images.slice(0, 6).map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`aspect-square overflow-hidden border-2 transition-colors ${
                      currentImageIndex === index ? 'border-blue-600' : 'border-transparent hover:border-zinc-300'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1655285162488-f495df383244?w=200';
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details - Right Side */}
          <div>
            {/* Header */}
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {vehicle.status === 'disponibile' && (
                  <Badge className="bg-green-600 text-white text-xs font-semibold uppercase tracking-wide">
                    Disponibile
                  </Badge>
                )}
                <Badge className="bg-zinc-900 text-white text-xs font-semibold uppercase tracking-wide">
                  {vehicle.vehicle_type === 'usato' ? 'Usato' : vehicle.vehicle_type}
                </Badge>
                <Badge className="bg-blue-600 text-white text-xs font-semibold uppercase tracking-wide">
                  {vehicle.emission_class?.toUpperCase()}
                </Badge>
              </div>

              <p className="text-blue-600 font-semibold uppercase tracking-widest text-sm mb-1">
                {vehicle.brand}
              </p>
              <h1 
                className="text-4xl md:text-5xl font-bold text-zinc-900 mb-2"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                data-testid="vehicle-title"
              >
                {vehicle.model}
              </h1>
              <p className="text-lg text-zinc-600">
                {vehicle.version}
              </p>
            </div>

            {/* Price */}
            <div className="bg-white border border-zinc-200 p-6 mb-6">
              <div className="flex items-end justify-between">
                <div>
                  <p 
                    className="text-5xl font-bold text-blue-600"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                    data-testid="vehicle-price"
                  >
                    {formatPrice(vehicle.price)}
                  </p>
                </div>
                <span className="text-sm text-zinc-500 uppercase tracking-wide">
                  IVA inclusa
                </span>
              </div>
            </div>

            {/* WhatsApp Contact Button */}
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}?text=${encodeURIComponent(
                `Richiesta info auto sito\n\nSono interessato a:\n${vehicle.brand} ${vehicle.model}\n${vehicle.version}\nPrezzo: ${formatPrice(vehicle.price)}\nAnno: ${getYear(vehicle.initial_registration)}\nKm: ${formatMileage(vehicle.mileage)}\n\nVorrei ricevere maggiori informazioni.`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block mb-3"
              data-testid="whatsapp-btn"
            >
              <Button 
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wider py-6 text-base"
              >
                <MessageCircle className="w-5 h-5 mr-3" />
                Richiedi Info su WhatsApp
              </Button>
            </a>

            {/* Phone Contact Button */}
            <a
              href="tel:+390571902390"
              className="block mb-3"
              data-testid="phone-btn"
            >
              <Button 
                variant="outline"
                className="w-full border-blue-600 text-blue-600 hover:bg-blue-50 font-bold uppercase tracking-wider py-6 text-base"
              >
                <Phone className="w-5 h-5 mr-3" />
                +39 0571 902390
              </Button>
            </a>

            {/* Email Contact Button */}
            <a
              href={`mailto:info@cavw.it?subject=${encodeURIComponent(`Richiesta info: ${vehicle.brand} ${vehicle.model}`)}&body=${encodeURIComponent(
                `Buongiorno,\n\nSono interessato al seguente veicolo:\n\n${vehicle.brand} ${vehicle.model}\n${vehicle.version}\nPrezzo: ${formatPrice(vehicle.price)}\nAnno: ${getYear(vehicle.initial_registration)}\nKm: ${formatMileage(vehicle.mileage)}\n\nVorrei ricevere maggiori informazioni.\n\nCordiali saluti`
              )}`}
              className="block mb-6"
              data-testid="email-btn"
            >
              <Button 
                variant="outline"
                className="w-full border-zinc-400 text-zinc-700 hover:bg-zinc-50 font-bold uppercase tracking-wider py-6 text-base"
              >
                <Mail className="w-5 h-5 mr-3" />
                Contattaci via Email
              </Button>
            </a>

            {/* Main Specs */}
            <div className="bg-white border border-zinc-200 p-6 mb-6">
              <h3 
                className="text-lg font-bold text-zinc-900 uppercase tracking-wide mb-4"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Caratteristiche Principali
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Immatricolazione</p>
                    <p className="font-semibold text-zinc-900">
                      {getMonth(vehicle.initial_registration)} {getYear(vehicle.initial_registration)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Gauge className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Chilometri</p>
                    <p className="font-semibold text-zinc-900">{formatMileage(vehicle.mileage)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Fuel className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Carburante</p>
                    <p className="font-semibold text-zinc-900">{getFuelLabel(vehicle.fuel_type)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Settings2 className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Cambio</p>
                    <p className="font-semibold text-zinc-900">
                      {vehicle.transmission === 'manual' ? 'Manuale' : 'Automatico'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Palette className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Colore</p>
                    <p className="font-semibold text-zinc-900">{vehicle.color}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Potenza</p>
                    <p className="font-semibold text-zinc-900">{vehicle.kilowatt} kW</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Technical Specs */}
            <div className="bg-white border border-zinc-200 p-6 mb-6">
              <h3 
                className="text-lg font-bold text-zinc-900 uppercase tracking-wide mb-4"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Dati Tecnici
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex justify-between py-2 border-b border-zinc-100">
                  <span className="text-zinc-500 text-sm">Cilindrata</span>
                  <span className="font-semibold text-zinc-900 text-sm font-mono">{vehicle.capacity} cc</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-100">
                  <span className="text-zinc-500 text-sm">Porte</span>
                  <span className="font-semibold text-zinc-900 text-sm font-mono">{vehicle.doors}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-100">
                  <span className="text-zinc-500 text-sm">Posti</span>
                  <span className="font-semibold text-zinc-900 text-sm font-mono">{vehicle.seats}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-100">
                  <span className="text-zinc-500 text-sm">Carrozzeria</span>
                  <span className="font-semibold text-zinc-900 text-sm capitalize">{vehicle.body_type}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-100">
                  <span className="text-zinc-500 text-sm">Classe emissioni</span>
                  <span className="font-semibold text-zinc-900 text-sm uppercase">{vehicle.emission_class}</span>
                </div>
              </div>
            </div>

            {/* Equipment */}
            {vehicle.equipments?.length > 0 && (
              <div className="bg-white border border-zinc-200 p-6">
                <h3 
                  className="text-lg font-bold text-zinc-900 uppercase tracking-wide mb-4"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Dotazioni ({vehicle.equipments.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto" data-testid="equipment-list">
                  {vehicle.equipments.map((eq, index) => (
                    <div key={index} className="flex items-start gap-2 py-1">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-zinc-700">{eq}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
